import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Layout, message } from 'antd';
import MediaViewerHeader from '@components/MediaViewerHeader';
import {
  createNewComment,
  removeComment,
  editComment,
  setCommentLike,
  setAsset,
  removeCommentAttachment,
  setAssetVariant
} from '@redux/actions/mediaViewerAction';
import {
  PageDto,
  AssetVersionCommentMentionDto,
  CreateAWCommentDto,
  ReplyAWCommentDto,
  ApprovalWorkflowItemDto
} from '@api/Api';
import { useAssetReview } from '@components/AssetReviewProvider';
import { useTypedSelector } from '@hooks';
import { nonNull } from '@helpers/non-null';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import { MediaViewerActionTypes } from '@redux/types/mediaViewerType';
import {
  approvalWorkflowAddNotificationSubscription,
  assetAwCommentsCreateAssetComment,
  assetAwCommentsDeleteAssetComment,
  assetAwCommentsGetAssetComment,
  assetAwCommentsListCommentPotentialMentions,
  assetAwCommentsReplyAssetComment,
  assetAwCommentsSetAssetCommentLike,
  assetAwCommentsUpdateAssetComment,
  getAssetByAw
} from '@api/Approval';
import { ApprovalReviewerPage } from '@pages/ApprovalReviewer/ApprovalReviewer';
import AccessClose from '@pages/ExternalReviewer/AccessClose';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import { assetSetRecentlyOpened } from '@api/Asset';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useFetch } from '@hooks/useFetch';
import { organizationItemStateSelector } from '@redux/selectors/organizations';
import {
  fetchOrganizationSeatsUsed,
  fetchOrganizationStorageUsed,
  fetchOrganizationSubscription
} from '@redux/actions/organizations';
import { apiClient } from '@api/client';
import WorkspacesPanel from '@components/WorkspacesPanel';
import { useAwCommentsAssetsQuery } from '@hooks/queries/approval';
import {
  MediaViewerOutletContextState,
  CreateAssetCommentParams,
  UpdateAssetCommentParams,
  SetAssetCommentLikeParams
} from '@layouts/types';

const { Header, Content, Sider } = Layout;

type ApprovalReviewerMediaLayoutProps = {
  layoutType: 'asset' | 'batch-assets-list';
  approvalWorkflow: ApprovalWorkflowItemDto;
  isOrgMember: boolean;
};

function ApprovalReviewerMediaLayout({
  layoutType,
  approvalWorkflow,
  isOrgMember
}: ApprovalReviewerMediaLayoutProps) {
  const isManager = approvalWorkflow.permissions.manage;
  if (!isManager) {
    const accessCloseReason = approvalWorkflow.isPaused
      ? 'paused'
      : approvalWorkflow.isFutureReview
      ? 'stage-not-started'
      : undefined;

    if (accessCloseReason)
      return (
        <ApprovalReviewerPage
          element={<AccessClose type="approval" awReason={accessCloseReason} />}
        />
      );
  }
  if (layoutType === 'batch-assets-list') {
    return (
      <ApprovalReviewerBatchAssetsList
        approvalWorkflow={approvalWorkflow}
        isOrgMember={isOrgMember}
      />
    );
  }
  if (layoutType === 'asset') {
    return (
      <ApprovalReviewerAsset
        approvalWorkflow={approvalWorkflow}
        isOrgMember={isOrgMember}
      />
    );
  }
  return null;
}

function ApprovalReviewerAsset({
  approvalWorkflow,
  isOrgMember
}: Omit<ApprovalReviewerMediaLayoutProps, 'layoutType'>) {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();

  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const { id: awId, organizationId } = approvalWorkflow;

  const { password } = useAssetReview();
  const assetId = asset?.id;
  const versionId = selectedVersion?.id;
  const hasAccessToAsset = approvalWorkflow.assets.some(
    (x) => x.id === assetId && x.hasAccess
  );

  const prevVersionId = useRef(versionId || null);

  const { data: commentsList, isLoading: isLoadingCommentsList } =
    useAwCommentsAssetsQuery(
      {
        assetVersionId: versionId || '',
        awId: awId || '',
        approvalWorkflowPassword: password
      },
      {
        keepPreviousData: versionId === prevVersionId.current,
        enabled: !!versionId && !!awId
      }
    );

  if (versionId !== prevVersionId.current && !isLoadingCommentsList) {
    prevVersionId.current = versionId || null;
  }

  useEffect(() => {
    (async () => {
      if (!hasAccessToAsset || !assetId || !versionId) return;
      await assetSetRecentlyOpened({
        assetId,
        assetVersionId: versionId
      });
    })();
  }, [hasAccessToAsset, assetId, versionId]);

  useEffect(() => {
    approvalWorkflowAddNotificationSubscription({
      id: awId,
      password
    });
  }, [awId, password]);

  const outletContext = useMemo<MediaViewerOutletContextState>(() => {
    const params = {
      awId,
      approvalWorkflowPassword: password
    };
    return {
      addNewComment: async (
        data: CreateAssetCommentParams,
        parentId: string
      ) => {
        try {
          let newComment = null;
          if (parentId && parentId !== 'new') {
            const {
              data: { comment }
            } = await assetAwCommentsReplyAssetComment({
              ...(data as ReplyAWCommentDto),
              ...params
            });
            newComment = comment;
          } else {
            const {
              data: { comment }
            } = await assetAwCommentsCreateAssetComment({
              ...(data as CreateAWCommentDto),
              ...params
            });
            newComment = comment;
          }
          return newComment;
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to create comment. Please try again.',
            className: 'message-dark-modal'
          });
          throw error;
        }
      },
      removeComment: async (id: string) => {
        try {
          await removeComment(id);
          await assetAwCommentsDeleteAssetComment({
            id,
            ...params
          });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to delete comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      editComment: async (data: UpdateAssetCommentParams) => {
        try {
          const {
            data: { comment }
          } = await assetAwCommentsUpdateAssetComment({
            ...data,
            ...params
          });
          await editComment(comment);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentLike: async (data: SetAssetCommentLikeParams, user: any) => {
        try {
          await setCommentLike(data, user);
          await assetAwCommentsSetAssetCommentLike({
            ...data,
            ...params
          });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update like status. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      getPotentialMentions: async ({
        assetVersionId,
        searchQuery
      }: {
        assetVersionId: string;
        searchQuery?: string;
      }): Promise<AssetVersionCommentMentionDto[]> => {
        const {
          data: { edges }
        }: {
          data: PageDto & { edges: { node: AssetVersionCommentMentionDto }[] };
        } = await assetAwCommentsListCommentPotentialMentions({
          assetVersionId,
          searchQuery,
          ...params
        });
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      getPotentialGroupMentions: async ({
        assetVersionId,
        searchQuery
      }: {
        assetVersionId: string;
        searchQuery?: string;
      }): Promise<AssetVersionCommentMentionDto[]> => {
        const {
          data: { edges }
        }: {
          data: PageDto & { edges: { node: AssetVersionCommentMentionDto }[] };
        } =
          await apiClient.approvalWorkflow.assetAwCommentsControllerListCommentPotentialGroupMentions(
            {
              assetVersionId,
              searchQuery,
              ...params
            }
          );
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      isLoadingCommentsList,
      commentsList: commentsList || []
    };
  }, [password, versionId, awId, isLoadingCommentsList, commentsList]);

  useLayoutEffect(() => {
    if (!selectedVersion?.id) return;
    dispatch({
      type: MediaViewerActionTypes.SET_RESET_ASSET_VERSION_STATE
    });
  }, [dispatch, selectedVersion?.id]);

  useFetch({
    disabled: !isOrgMember,
    key: `organizations-subscription-${organizationId}`,
    selector: (state) =>
      organizationItemStateSelector(state, { organizationId }).subscription
        .fetch,
    fetch: (fetchType) =>
      dispatch(
        fetchOrganizationSubscription({
          fetchType,
          organizationId
        })
      )
  });
  useFetch({
    disabled: !isOrgMember,
    key: `organizations-seats-used-${organizationId}`,
    selector: (state) =>
      organizationItemStateSelector(state, { organizationId }).seatsCount
        .countFetch,
    fetch: (fetchType) =>
      dispatch(
        fetchOrganizationSeatsUsed({
          fetchType,
          organizationId
        })
      )
  });
  useFetch({
    disabled: !isOrgMember,
    key: `organizations-storage-used-${organizationId}`,
    selector: (state) =>
      organizationItemStateSelector(state, { organizationId }).assetsSummarySize
        .sizeFetch,
    fetch: (fetchType) =>
      dispatch(
        fetchOrganizationStorageUsed({
          fetchType,
          organizationId
        })
      )
  });

  const assetIdRef = useRef(assetId);
  assetIdRef.current = assetId;

  const versionIdRef = useRef(selectedVersion?.id);
  versionIdRef.current = selectedVersion?.id;

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetIdRef.current) return false;
    if (msg.payload.assetVersionId !== versionIdRef.current) return false;
    if (msg.causedByMe) return;
    const {
      data: { comment }
    } = await assetAwCommentsGetAssetComment({
      id: msg.payload.commentId,
      awId: nonNull(awId),
      approvalWorkflowPassword: password
    });
    if (msg.payload.assetId !== assetIdRef.current) return false;
    if (msg.payload.assetVersionId !== versionIdRef.current) return false;
    await createNewComment(comment, versionIdRef.current || '');
  }, 'asset-comment-created');

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetIdRef.current) return false;
    if (msg.payload.assetVersionId !== versionIdRef.current) return false;
    if (msg.causedByMe) return;

    await removeComment(msg.payload.commentId);
  }, 'asset-comment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.target.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.payload.target.assetId !== assetIdRef.current) return;
      const {
        data: { comment }
      } = await assetAwCommentsGetAssetComment({
        id: lastMessage.payload.target.commentId,
        awId: nonNull(awId),
        approvalWorkflowPassword: password
      });
      if (lastMessage.payload.target.assetId !== assetIdRef.current) return;
      await editComment(comment);
    },
    600,
    (msg) => {
      if (msg.payload.target.assetId !== assetIdRef.current) return false;
      if (!msg.payload.target.commentId) return false;
      return true;
    },
    'attachment-created'
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.target.assetId !== assetIdRef.current) return false;
    if (msg.causedByMe) return;
    await removeCommentAttachment({
      commentId: msg.payload.target.commentId,
      attachmentId: msg.payload.attachmentId
    });
  }, 'attachment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.payload.assetId !== assetIdRef.current) return false;
      if (lastMessage.payload.assetVersionId !== versionIdRef.current)
        return false;
      const {
        data: { comment }
      } = await assetAwCommentsGetAssetComment({
        id: lastMessage.payload.commentId,
        awId: nonNull(awId),
        approvalWorkflowPassword: password
      });
      if (lastMessage.payload.assetId !== assetIdRef.current) return false;
      if (lastMessage.payload.assetVersionId !== versionIdRef.current)
        return false;
      await editComment(comment);
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetIdRef.current) return false;
      if (msg.payload.assetVersionId !== versionIdRef.current) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-comment-updated'
  );

  const modalState = useTypedSelector(({ modal }) => modal.approvalModal);
  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async (messages) => {
      if (!awId) return;
      if (!assetIdRef.current) return;
      const msgPayload = messages?.[0]?.payload;
      if (msgPayload.assetId !== assetIdRef.current) return false;
      const newVersionId: string | undefined = messages
        .reverse()
        .find((x) => x.payload.reason === 'new-version')
        ?.payload.assetVersionId;
      let reloadCurrentVersion = !newVersionId;
      if (newVersionId) {
        try {
          const {
            data: { asset }
          } = await getAssetByAw({
            awId,
            assetId: assetIdRef.current,
            password
          });
          if (msgPayload.assetId !== assetIdRef.current) return false;
          dispatch(setAsset(asset));
          dispatch(setAssetVariant(newVersionId));
          if (modalState && modalState.visible) {
            dispatch(toggleApprovalModal({ ...modalState, visible: false }));
          }
          navigate(`/approval?id=${awId}&assetId=${asset.id}`, {
            replace: true
          });
          return;
        } catch {
          reloadCurrentVersion = true;
        }
      }
      if (reloadCurrentVersion) {
        try {
          const versionId = versionIdRef.current;
          if (!versionId) return;
          const { data } = await getAssetByAw({
            awId,
            assetId: assetIdRef.current,
            password
          });
          if (msgPayload.assetId !== assetIdRef.current) return false;
          const versions = data?.asset?.asset?.versions || [];

          if (!versions?.length) return false;
          const existVersion = versions.find((item) => item.id === versionId);
          dispatch(setAsset(data.asset));
          dispatch(
            setAssetVariant(existVersion ? existVersion.id : versions[0].id)
          );
        } catch {
          window.location.reload();
        }
      }
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetIdRef.current) return false;
      if (
        msg.causedByMe &&
        msg.payload.reason !== 'version-removed-from-stack' &&
        msg.payload.reason !== 'conversion-status-changed' &&
        msg.payload.reason !== 'new-version'
      )
        return false;

      return true;
    },
    'asset-updated'
  );

  if (!selectedVersion) return null;

  return (
    <Layout
      style={{ background: '#1A1C1E', color: '#fff', minHeight: '100vh' }}
    >
      {isOrgMember && (
        <Sider
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0
          }}
          className="workspaces_sider b-bg-dark"
          width={65}
        >
          <WorkspacesPanel />
        </Sider>
      )}
      <Layout
        className="site-layout external-reviewer-media"
        style={{ background: '#1A1C1E', paddingLeft: isOrgMember ? 65 : 0 }}
      >
        <Header
          className="site-layout-background b-background-black"
          style={{
            padding: 0,
            width: isOrgMember ? 'calc(100% - 65px)' : '100%'
          }}
        >
          <MediaViewerHeader
            view={isOrgMember ? 'approval-asset' : 'external-approval-asset'}
            password={password}
            approvalWorkflowId={awId}
          />
        </Header>
        <Content style={{ marginTop: 70 }}>
          <Outlet context={outletContext} />
        </Content>
      </Layout>
    </Layout>
  );
}

function ApprovalReviewerBatchAssetsList({
  approvalWorkflow,
  isOrgMember
}: Omit<ApprovalReviewerMediaLayoutProps, 'layoutType'>) {
  const { password } = useAssetReview();
  return (
    <Layout
      style={{ background: '#1A1C1E', color: '#fff', minHeight: '100vh' }}
    >
      {isOrgMember && (
        <Sider
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0
          }}
          className="workspaces_sider b-bg-dark"
          width={65}
        >
          <WorkspacesPanel />
        </Sider>
      )}
      <Layout
        className="site-layout external-reviewer-media"
        style={{ background: '#1A1C1E', paddingLeft: isOrgMember ? 65 : 0 }}
      >
        <Header
          className="site-layout-background b-background-black"
          style={{
            padding: 0,
            width: isOrgMember ? 'calc(100% - 65px)' : '100%'
          }}
        >
          <MediaViewerHeader
            view={
              isOrgMember
                ? 'approval-assets-list'
                : 'external-approval-assets-list'
            }
            approvalWorkflowId={approvalWorkflow.id}
            password={password}
          />
        </Header>
        <Content style={{ marginTop: 70 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}

export default ApprovalReviewerMediaLayout;
